/* import __COLOCATED_TEMPLATE__ from './create-replacement-order-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import ajax from 'embercom/lib/ajax';
import ENV from 'embercom/config/environment';
import type IntlService from 'ember-intl/services/intl';
import type Session from 'embercom/services/session';
import { captureException } from 'embercom/lib/sentry';
import ShopifyReplacementOrderIncomplete from 'embercom/components/notifications/shopify-replacement-order-incomplete';
import type {
  ShopifyOrder,
  ShopifyLineItem,
  ShippingAddress,
} from 'embercom/components/inbox2/conversation-details-sidebar/sidebar-sections/shopify';
import {
  buildPerItemNote,
  hasAllItemReasons,
  type LineItemReason,
} from 'embercom/lib/inbox/shopify/compensation-reasons';

interface SelectedLineItem {
  lineItemId: number;
  quantity: number;
}

interface CreateReplacementOrderModalArgs {
  isOpen: boolean;
  onModalClose: () => void;
  order: ShopifyOrder;
  shopifyDomain: string | null;
  onReplacementOrderSuccess?: () => void;
  conversationId?: number;
}

export default class CreateReplacementOrderModal extends Component<CreateReplacementOrderModalArgs> {
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare session: Session;
  @service declare intl: IntlService;

  @tracked note = '';
  @tracked selectedLineItems: SelectedLineItem[] = [];
  @tracked editedShippingAddress: ShippingAddress | null = null;
  @tracked useDifferentAddress = false;
  @tracked lineItemReasons: LineItemReason[] = [];
  @tracked isSelectAllChecked = false;
  @tracked genericRefundReason = '';
  @tracked workspaceRefundReasons: string[] = [];
  @tracked workspaceRefundReasonsFetchError: string | null = null;

  get isPerItemRefundReasonEnabled() {
    return this.session.workspace.isFeatureEnabled('tdf-shopify-per-item-refund-reason');
  }

  get workspaceReasonOptions() {
    return this.workspaceRefundReasons.map((reason) => ({
      value: reason,
      label: reason,
    }));
  }

  get compensationNote(): string {
    if (this.isSelectAllChecked && this.genericRefundReason) {
      return this.genericRefundReason;
    }
    if (this.isPerItemRefundReasonEnabled) {
      return buildPerItemNote(
        this.selectedLineItems,
        this.lineItemReasons,
        this.args.order.line_items,
      );
    }
    return this.note;
  }

  constructor(owner: unknown, args: CreateReplacementOrderModalArgs) {
    super(owner, args);
    this.resetModalData();

    if (this.isPerItemRefundReasonEnabled) {
      void taskFor(this.fetchWorkspaceRefundReasons).perform();
    }
  }

  get currencySymbol() {
    return this.args.order.total?.charAt(0) ?? '$';
  }

  get hasSelectedItems() {
    return this.selectedLineItems.some((item) => item.quantity > 0);
  }

  get isCreateReplacementOrderTaskRunning() {
    return taskFor(this.createReplacementOrderTask).isRunning;
  }

  get isFetchingWorkspaceRefundReasons(): boolean {
    return taskFor(this.fetchWorkspaceRefundReasons).isRunning;
  }

  get hasWorkspaceRefundReasonsError(): boolean {
    return Boolean(this.workspaceRefundReasonsFetchError);
  }

  get shouldShowEmptyReasonsGuidance(): boolean {
    return (
      !this.isFetchingWorkspaceRefundReasons &&
      !this.hasWorkspaceRefundReasonsError &&
      this.workspaceRefundReasons.length === 0 &&
      this.isPerItemRefundReasonEnabled &&
      !this.isSelectAllChecked
    );
  }

  get hasAllRequiredReasons(): boolean {
    if (this.isSelectAllChecked) {
      return Boolean(this.genericRefundReason);
    }
    if (!this.isPerItemRefundReasonEnabled) {
      return Boolean(this.note.trim());
    }
    return hasAllItemReasons(this.selectedLineItems, this.lineItemReasons);
  }

  get isLineItemReplaceable() {
    return (lineItem: ShopifyLineItem): boolean => {
      let alreadyRefunded = lineItem.already_refunded ?? 0;
      let quantityReplaceable = lineItem.quantity_refundable ?? lineItem.quantity - alreadyRefunded;
      return quantityReplaceable > 0;
    };
  }

  get shippingAddress(): ShippingAddress {
    return (
      this.editedShippingAddress ||
      this.args.order.shipping_address || {
        first_name: null,
        last_name: null,
        address1: null,
        address2: null,
        city: null,
        province: null,
        country: null,
        zip: null,
        phone: null,
      }
    );
  }

  get formattedOriginalAddress(): string {
    let address = this.args.order.shipping_address;
    if (!address) {
      return this.intl.t(
        'inbox.conversation-details-sidebar.shopify.create-replacement-order-modal.no-shipping-address',
      );
    }

    let parts: string[] = [];
    if (address.first_name || address.last_name) {
      parts.push([address.first_name, address.last_name].filter(Boolean).join(' '));
    }
    if (address.address1) {
      parts.push(address.address1);
    }
    if (address.address2) {
      parts.push(address.address2);
    }
    let cityLine = [address.city, address.province, address.zip].filter(Boolean).join(', ');
    if (cityLine) {
      parts.push(cityLine);
    }
    if (address.country) {
      parts.push(address.country);
    }
    return parts.join('\n');
  }

  get isAddressValid(): boolean {
    if (!this.useDifferentAddress) {
      return true;
    }
    let address = this.shippingAddress;
    return Boolean(address.address1?.trim() && address.city?.trim() && address.country?.trim());
  }

  get getLineItemReason() {
    void this.lineItemReasons.length;

    return (lineItemId: number): string => {
      return this.lineItemReasons.find((r) => r.lineItemId === lineItemId)?.reason ?? '';
    };
  }

  @action
  isLineItemSelected(lineItemId: number): boolean {
    let selectedItem = this.selectedLineItems.find((item) => item.lineItemId === lineItemId);
    return selectedItem ? selectedItem.quantity > 0 : false;
  }

  @action
  getSelectedQuantity(lineItemId: number): number {
    let selectedItem = this.selectedLineItems.find((item) => item.lineItemId === lineItemId);
    return selectedItem?.quantity || 0;
  }

  @action
  toggleLineItemSelection(lineItemId: number) {
    let isCurrentlySelected = this.isLineItemSelected(lineItemId);
    let lineItem = this.args.order.line_items.find((item) => item.id === lineItemId);

    if (!lineItem) {
      return;
    }

    let targetQuantity = isCurrentlySelected ? 0 : lineItem.quantity;
    this.updateLineItemQuantityValue(lineItemId, targetQuantity);
  }

  @action
  updateLineItemQuantity(lineItemId: number, event: Event) {
    let target = event.target as HTMLInputElement;
    let quantity = parseInt(target.value, 10);
    this.updateLineItemQuantityValue(lineItemId, quantity);
  }

  private updateLineItemQuantityValue(lineItemId: number, quantity: number) {
    let existingItemIndex = this.selectedLineItems.findIndex(
      (item) => item.lineItemId === lineItemId,
    );

    if (existingItemIndex !== -1) {
      this.selectedLineItems[existingItemIndex] = {
        ...this.selectedLineItems[existingItemIndex],
        quantity: Math.max(0, quantity),
      };
    } else {
      this.selectedLineItems = [
        ...this.selectedLineItems,
        {
          lineItemId,
          quantity: Math.max(0, quantity),
        },
      ];
    }

    this.selectedLineItems = [...this.selectedLineItems];
    this.isSelectAllChecked = false;
  }

  @action
  updateLineItemReason(lineItemId: number, event: Event) {
    let target = event.target as HTMLSelectElement;
    let reason = target.value;
    let existingIndex = this.lineItemReasons.findIndex((r) => r.lineItemId === lineItemId);

    if (existingIndex >= 0) {
      this.lineItemReasons = this.lineItemReasons.map((r) =>
        r.lineItemId === lineItemId ? { ...r, reason } : r,
      );
    } else {
      this.lineItemReasons = [...this.lineItemReasons, { lineItemId, reason }];
    }
  }

  @action
  updateShippingAddressField(field: keyof ShippingAddress, event: Event) {
    let target = event.target as HTMLInputElement;
    let value = target.value;

    if (!this.editedShippingAddress) {
      this.editedShippingAddress = { ...this.shippingAddress };
    }

    this.editedShippingAddress = {
      ...this.editedShippingAddress,
      [field]: value || null,
    };
  }

  @action
  retryFetchWorkspaceRefundReasons() {
    void taskFor(this.fetchWorkspaceRefundReasons).perform();
  }

  @action
  toggleSelectAll() {
    this.isSelectAllChecked = !this.isSelectAllChecked;
    if (this.isSelectAllChecked) {
      if (
        this.workspaceRefundReasons.length === 0 &&
        !taskFor(this.fetchWorkspaceRefundReasons).isRunning
      ) {
        void taskFor(this.fetchWorkspaceRefundReasons).perform();
      }
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id && this.isLineItemReplaceable(item))
        .map((item) => ({
          lineItemId: item.id,
          quantity: item.quantity_refundable ?? item.quantity,
        }));
      this.lineItemReasons = [];
    } else {
      this.selectedLineItems = this.args.order.line_items
        .filter((item) => item.id)
        .map((item) => ({ lineItemId: item.id, quantity: 0 }));
    }
  }

  @action
  updateGenericRefundReason(event: Event) {
    let target = event.target as HTMLSelectElement;
    this.genericRefundReason = target.value;
  }

  @action
  resetModalData() {
    this.note = '';
    this.editedShippingAddress = null;
    this.useDifferentAddress = false;
    this.lineItemReasons = [];
    this.isSelectAllChecked = false;
    this.genericRefundReason = '';
    this.selectedLineItems = this.args.order.line_items
      .filter((item) => item.id)
      .map((item) => ({
        lineItemId: item.id,
        quantity: 0,
      }));
  }

  @action
  toggleUseDifferentAddress() {
    this.useDifferentAddress = !this.useDifferentAddress;
    if (this.useDifferentAddress && !this.editedShippingAddress) {
      this.editedShippingAddress = { ...this.shippingAddress };
    }
  }

  @action
  handleModalClose() {
    this.resetModalData();
    this.args.onModalClose();
  }

  @restartableTask
  *fetchWorkspaceRefundReasons(): TaskGenerator<void> {
    this.workspaceRefundReasonsFetchError = null;

    try {
      let response: any = yield ajax({
        type: 'GET',
        url: `/ember/shopify/integrations/workspace_refund_reasons?app_id=${this.session.workspace.id}`,
      });

      if (response && response.reasons) {
        this.workspaceRefundReasons = response.reasons;
      }
    } catch (error: unknown) {
      captureException(error);
      this.workspaceRefundReasonsFetchError = this.intl.t(
        'inbox.conversation-details-sidebar.shopify.refund-order-modal.refund-reasons-fetch-error',
      );
    }
  }

  @restartableTask
  *createReplacementOrderTask(): TaskGenerator<void> {
    try {
      let itemsToAdd = this.selectedLineItems.filter((item) => item.quantity > 0);

      if (itemsToAdd.length === 0) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.create-replacement-order-modal.errors.no-items-selected',
          ),
        );
        return;
      }

      if (!this.compensationNote.trim()) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.create-replacement-order-modal.errors.no-reason',
          ),
        );
        return;
      }

      if (this.useDifferentAddress && !this.isAddressValid) {
        this.notificationsService.notifyError(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.create-replacement-order-modal.errors.invalid-address',
          ),
        );
        return;
      }

      let lineItemsData = itemsToAdd.map((item) => ({
        line_item_id: item.lineItemId,
        quantity: item.quantity,
      }));

      let lineItemsDetail = itemsToAdd.map((item) => {
        let lineItem = this.args.order.line_items.find((li) => li.id === item.lineItemId);
        return {
          title: lineItem?.title,
          quantity: item.quantity,
          price: lineItem?.price,
        };
      });

      let shippingAddressToSend = this.useDifferentAddress
        ? this.editedShippingAddress
        : this.args.order.shipping_address;

      let response: $TSFixMe = yield ajax({
        url: '/ember/inbox/shopify/create_replacement_order',
        type: 'POST',
        data: JSON.stringify({
          app_id: this.session.workspace.id,
          original_order_id: this.args.order.id,
          order_name: this.args.order.name,
          conversation_id: this.args.conversationId,
          line_items: lineItemsData,
          line_items_detail: lineItemsDetail,
          reason: this.compensationNote,
          shopify_domain: this.args.shopifyDomain,
          shipping_address: shippingAddressToSend,
        }),
      });

      if (response.success) {
        this.notificationsService.notifyConfirmation(
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.create-replacement-order-modal.success',
            {
              originalOrderName: response.original_order_name,
            },
          ),
        );
      } else {
        let notificationDuration = ENV.APP._1000MS * 30;
        this.notificationsService.notifyWarningWithModelAndComponent(
          {
            url: response.draft_order_url,
            name: response.draft_order_name,
          },
          ShopifyReplacementOrderIncomplete,
          notificationDuration,
        );
      }

      this.intercomEventService.trackAnalyticsEvent({
        action: 'created_replacement_order',
        object: 'shopify_order',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
        items_count: itemsToAdd.length,
        original_order_id: this.args.order.id,
      });

      this.handleModalClose();
      this.args.onReplacementOrderSuccess?.();
    } catch (error) {
      this.notificationsService.notifyError(
        (error as Error).message ||
          this.intl.t(
            'inbox.conversation-details-sidebar.shopify.create-replacement-order-modal.errors.generic',
          ),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::CreateReplacementOrderModal': typeof CreateReplacementOrderModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/create-replacement-order-modal': typeof CreateReplacementOrderModal;
  }
}
